import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Shirt, Sparkles, ArrowLeft, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';

import '../../assets/css/previewtab.css';
import '../../assets/css/placement.css';

import DesignOverlay from '../design/DesignOverlay';


// Read-only summary view.
// - Always renders from the persisted state in TShirtCustomizer.
// - Never mutates placementTexts / placementImages / placementList.
const TShirtPreview = ({
  placementList = [],
  placementTexts = {},
  placementImages = {},
  currentViews = {},
  methodPositions = {},
  boxSize = {},
  setActiveStep = () => { },
}) => {
  const views = ['front', 'back', 'left', 'right'];

  // Default requirement: front preview should be open by default.
  const [activeView, setActiveView] = useState('front');

  const hasAnyDesign = placementList.length > 0;

  const methodsForActiveView = useMemo(() => {
    return placementList.filter((item) => item.placement === activeView);
  }, [placementList, activeView]);

  const previewCardRef = useRef(null);
  const imageContainerRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Captures only the t-shirt image + design overlays (no card border,
  // no placement label, no method badges) for a given view.
  const captureViewImage = useCallback(async (view) => {
    // Switch to the target view
    setActiveView(view);

    // Wait for React to re-render with the new view
    await new Promise((resolve) => setTimeout(resolve, 400));

    const container = imageContainerRef.current;
    if (!container) return null;

    const canvas = await html2canvas(container, {
      scale: 1,
      useCORS: true,
      allowTaint: false,
      backgroundColor: null, // transparent background
      logging: false,
    });

    return canvas.toDataURL('image/png');
  }, []);

  const handleDownloadZip = useCallback(async () => {
    setIsDownloading(true);

    try {
      const currentView = activeView;
      const zip = new JSZip();

// Capture all 4 views (front, back, left, right)
      for (const view of views) {
        const dataUrl = await captureViewImage(view);
        if (dataUrl) {
          // Convert base64 data URL to blob for JSZip
          const base64Data = dataUrl.split(',')[1];
          zip.file(`${view}-preview.png`, base64Data, { base64: true });
        }
      }

      // Generate and download the zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `teeprint-previews-${Date.now()}.zip`;
      link.href = URL.createObjectURL(zipBlob);
      link.click();

      // Clean up the object URL
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);

      // Restore original view
      setActiveView(currentView);
    } catch (error) {
      console.error('Failed to download zip:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [activeView, views, placementList, captureViewImage]);

  return (
    <div className="preview-tab">
      <div className="preview-tab-header">
        <div className="preview-tab-heading">
          <Sparkles size={18} className="preview-tab-heading-icon" />
          <div>
            <h2>Design Preview</h2>
            <p>Review your artwork across every print location before checkout</p>
          </div>
        </div>

        <div className="preview-tab-actions">
          {hasAnyDesign && (
            <>
              <button
                type="button"
                className="preview-download-btn"
                onClick={handleDownloadZip}
                disabled={isDownloading}
              >
                <Download size={16} />
                <span>{isDownloading ? 'Downloading...' : 'Download ZIP'}</span>
              </button>

              <div className="preview-tab-count">
                {placementList.length} {placementList.length === 1 ? 'location' : 'locations'} customized
              </div>
            </>
          )}
        </div>
      </div>

      {!hasAnyDesign ? (
        <div className="preview-empty-state">
          <Shirt size={40} className="preview-empty-icon" />
          <h3>Nothing to preview yet</h3>
          <p>Add text or upload an image on the Design tab and it will show up here.</p>
          <button
            type="button"
            className="preview-empty-cta"
            onClick={() => setActiveStep('design')}
          >
            <ArrowLeft size={16} />
            <span>Back to Design</span>
          </button>
        </div>
      ) : (
        <div className="preview-single-view">
          {/* Placement buttons like design */}
          <div className="preview-thumbnail-list">
            {views.map((placementId) => (
              <button
                key={placementId}
                type="button"
                className={`preview-thumbnail ${activeView === placementId ? "active" : ""
                  }`}
                onClick={() => setActiveView(placementId)}
              >
                <div className="preview-thumbnail-image">
                  <img
                    src={currentViews[placementId]}
                    alt={placementId}
                  />
                </div>

                <span className="preview-thumbnail-label">
                  {placementId}
                </span>
              </button>
            ))}
          </div>
          <div className="preview-layout">
            <div className="preview-main">
              <div className="preview-single-view-card" ref={previewCardRef}>
                <div className="preview-single-view-image" ref={imageContainerRef}>
                  <img src={currentViews[activeView]} alt={activeView} />

                  <DesignOverlay
                    activeView={activeView}
                    placementList={placementList}
                    placementTexts={placementTexts}
                    placementImages={placementImages}
                    methodPositions={methodPositions}
                    boxSize={boxSize}
                  />
                </div>

                {/* <div className="preview-view-footer">
                  <span className="preview-view-label">{activeView}</span>
                  <div className="preview-methods">
                    {methodsForActiveView.length ? (
                      methodsForActiveView.map((method) => (
                        <span key={method.key} className="preview-method-badge">
                          {method.method === 'print'
                            ? 'Print'
                            : method.method === 'embroidery'
                              ? 'Embroidery'
                              : method.method}
                        </span>
                      ))
                    ) : (
                      <span className="preview-method-badge preview-method-badge--muted">Blank</span>
                    )}
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TShirtPreview;

